/**
 * SOQL Query Editor with Monaco + ANTLR intellisense.
 *
 * Features:
 * - Syntax highlighting for SOQL/SQL hybrid
 * - Autocomplete for objects, fields, relationships
 * - Real-time error validation
 * - Ctrl+Enter to execute
 */

import { useRef, useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import Editor, { loader } from '@monaco-editor/react';
import { registerSOQLLanguage, SOQL_LANGUAGE_ID } from './SOQLLanguage.js';
import { createSOQLCompletionProvider } from './SOQLCompletionProvider.js';
import { validateSOQL } from './SOQLDiagnosticsProvider.js';

// Track if we've registered the language
let languageRegistered = false;

const SOQLEditor = forwardRef(function SOQLEditor({
  value = '',
  onChange,
  onExecute,
  metadata = null,
  height = '200px',
  readOnly = false,
  placeholder = "SELECT Id, Name FROM Account WHERE ...",
}, ref) {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const completionProviderDisposableRef = useRef(null);
  const [monacoInstance, setMonacoInstance] = useState(null);

  useImperativeHandle(ref, () => ({
    insertAtCursor(text) {
      const editor = editorRef.current;
      if (!editor) return;
      // getSelection() returns a zero-width range at cursor when nothing is selected,
      // or the full selection range when text is highlighted — executeEdits replaces it.
      const selection = editor.getSelection();
      editor.executeEdits('builder', [{ range: selection, text }]);
      editor.focus();
    },
    isEmpty() {
      return !editorRef.current?.getValue()?.trim();
    },
  }));

  // Handle editor mount
  const handleEditorDidMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    setMonacoInstance(monaco);

    // Register SOQL language once
    if (!languageRegistered) {
      registerSOQLLanguage(monaco);
      languageRegistered = true;
    }

    // Set up Ctrl+Enter to execute
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
      onExecute?.();
    });

    // Set up validation on change
    editor.onDidChangeModelContent(() => {
      if (metadata) {
        const model = editor.getModel();
        if (model) {
          const markers = validateSOQL(model, metadata);
          monaco.editor.setModelMarkers(model, 'soql', markers);
        }
      }
    });

    // Initial validation
    if (metadata) {
      const model = editor.getModel();
      if (model) {
        const markers = validateSOQL(model, metadata);
        monaco.editor.setModelMarkers(model, 'soql', markers);
      }
    }
  }, [metadata, onExecute]);

  // Update completion provider when metadata changes
  useEffect(() => {
    const monaco = monacoInstance;
    if (monaco && metadata) {
      const provider = createSOQLCompletionProvider(metadata);
      const disposable = monaco.languages.registerCompletionItemProvider(
        SOQL_LANGUAGE_ID,
        provider
      );
      completionProviderDisposableRef.current = disposable;

      // Re-validate
      const editor = editorRef.current;
      if (editor) {
        const model = editor.getModel();
        if (model) {
          const markers = validateSOQL(model, metadata);
          monaco.editor.setModelMarkers(model, 'soql', markers);
        }
      }

      return () => {
        disposable.dispose();
        if (completionProviderDisposableRef.current === disposable) {
          completionProviderDisposableRef.current = null;
        }
      };
    }
    return undefined;
  }, [metadata, monacoInstance]);

  return (
    <div style={{ height, width: '100%' }} data-testid="soql-editor">
      <Editor
        height="100%"
        defaultLanguage={SOQL_LANGUAGE_ID}
        defaultValue={value}
        value={value}
        onChange={onChange}
        onMount={handleEditorDidMount}
        theme="vs-dark"
        options={{
          minimap: { enabled: false },
          lineNumbers: 'on',
          scrollBeyondLastLine: false,
          automaticLayout: true,
          fontSize: 14,
          tabSize: 2,
          wordWrap: 'on',
          readOnly,
          suggestOnTriggerCharacters: true,
          quickSuggestions: true,
          padding: { top: 12, bottom: 12 },
          renderLineHighlight: 'line',
          scrollbar: {
            vertical: 'auto',
            horizontal: 'auto',
          },
        }}
      />
    </div>
  );
});

export default SOQLEditor;

/**
 * Simple fallback editor (no Monaco) for environments where Monaco can't load.
 */
export function SOQLEditorFallback({
  value = '',
  onChange,
  onExecute,
  height = '200px',
  readOnly = false,
  placeholder = "SELECT Id, Name FROM Account WHERE ...",
}) {
  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      onExecute?.();
    }
  };

  return (
    <textarea
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      onKeyDown={handleKeyDown}
      readOnly={readOnly}
      placeholder={placeholder}
      style={{
        height,
        width: '100%',
        padding: '12px',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        fontSize: '14px',
        backgroundColor: '#1e1e1e',
        color: '#d4d4d4',
        border: '1px solid #374151',
        borderRadius: '4px',
        resize: 'vertical',
      }}
    />
  );
}
